if (!customElements.get('techtees-cart-feedback')) {
  customElements.define('techtees-cart-feedback', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.status = this.querySelector('[data-tt-cart-status]');
      this.summary = this.querySelector('[data-tt-cart-summary]');
      this.actions = this.querySelector('[data-tt-cart-actions]');
      this.refreshButton = this.querySelector('button');
      this.refreshButton.hidden = false;
      this.refreshButton.addEventListener('click', () => this.refresh(), options);
      for (const name of ['tt:cart-added', 'tt:cart-reconciled', 'cart:updated', 'product-ajax:added']) {
        document.addEventListener(name, () => {
          if (name === 'tt:cart-added' || name === 'product-ajax:added') this.lastAdded = Date.now();
          return this.refresh();
        }, options);
      }
      document.addEventListener('change', event => {
        if (event.target.matches?.('[name="updates[]"]')) this.invalidate();
      }, options);
      document.addEventListener('click', event => {
        if (event.target.closest?.('m-cart-remove-button')) this.invalidate();
      }, options);
      window.addEventListener('pageshow', event => {
        if (event.persisted) this.invalidate();
      }, options);
    }
    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      clearTimeout(this.timeout);
      this.revision = (this.revision || 0) + 1;
    }
    showRecovery(show) {
      this.actions.hidden = !show;
      if (show) this.setAttribute('data-active', '');
      else this.removeAttribute('data-active');
    }
    invalidate() {
      this.showRecovery(true);
      this.request?.abort();
      clearTimeout(this.timeout);
      this.revision = (this.revision || 0) + 1;
      this.summary.hidden = true;
      this.refreshButton.disabled = false;
      this.removeAttribute('aria-busy');
      this.status.textContent = 'Cart may have changed. Check cart status for current totals.';
    }
    static valid(cart) {
      return cart && Array.isArray(cart.items) && Number.isSafeInteger(cart.item_count) && cart.item_count >= 0 &&
        Number.isSafeInteger(cart.total_price) && cart.total_price >= 0 && /^[A-Z]{3}$/.test(cart.currency) &&
        cart.items.every(item => Number.isSafeInteger(item.quantity) && item.quantity > 0) &&
        cart.items.reduce((sum, item) => sum + item.quantity, 0) === cart.item_count;
    }
    async refresh() {
      this.request?.abort();
      clearTimeout(this.timeout);
      const revision = this.revision = (this.revision || 0) + 1;
      this.request = new AbortController();
      this.timeout = setTimeout(() => this.request?.abort(), 15000);
      this.summary.hidden = true;
      this.refreshButton.disabled = true;
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Checking cart…';
      try {
        const url = new URL(this.dataset.cartUrl, window.location.origin);
        if (url.origin !== window.location.origin) throw new Error('Invalid cart route');
        url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
        const response = await fetch(url.href, { credentials: 'same-origin', cache: 'no-store', signal: this.request.signal });
        if (!response.ok) throw new Error('Cart read failed');
        const cart = await response.json();
        if (!this.constructor.valid(cart)) throw new Error('Invalid cart response');
        const total = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cart.currency }).format(cart.total_price / 100);
        if (!this.isConnected || revision !== this.revision) return;
        const items = `${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`;
        const text = cart.item_count === 0 ? this.dataset.empty : `${items} · ${this.dataset.subtotal}: ${total}`;
        const lead = this.lastAdded && Date.now() - this.lastAdded < 4000 ? 'Added' : 'Cart updated';
        this.summary.textContent = text;
        this.status.textContent = cart.item_count === 0 ? `Current cart: ${text}` : `${lead}. Cart has ${items}, subtotal ${total}.`;
        this.showRecovery(false);
      } catch {
        if (this.isConnected && revision === this.revision) {
          this.showRecovery(true);
          this.status.textContent = 'Cart status could not be confirmed. View cart before retrying an addition.';
        }
      } finally {
        if (revision === this.revision) {
          clearTimeout(this.timeout);
          this.refreshButton.disabled = false;
          this.removeAttribute('aria-busy');
        }
      }
    }
  });
}
